import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  PiCheckCircleLight,
  PiDownloadSimpleLight,
  PiEyeLight,
  PiFileTextLight,
  PiGaugeLight,
  PiPencilSimpleLineLight,
  PiTargetLight,
  PiWarningCircleLight,
} from "react-icons/pi";
import client from "../../api/client";
import FeatureFeedbackBox from "../../components/Generic/FeatureFeedbackBox";

const BACKEND = process.env.REACT_APP_DOMAIN_URL || "";

function ScoreBadge({ score }) {
  const color =
    score >= 80
      ? "bg-emerald-50 text-emerald-700 border-emerald-100"
      : score >= 60
      ? "bg-amber-50 text-amber-700 border-amber-100"
      : "bg-rose-50 text-rose-700 border-rose-100";

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-semibold ${color}`}>
      <PiGaugeLight size={18} />
      ATS Score: {score}/100
    </div>
  );
}

function ProcessingCard({ title, subtitle }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 text-center">
      <div className="w-10 h-10 border-2 border-[#F05A19] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
      <h2 className="text-base font-semibold text-gray-800 mb-1">{title}</h2>
      <p className="text-sm text-gray-500 max-w-sm mx-auto">{subtitle}</p>
    </div>
  );
}

function ListSection({ icon: Icon, title, items }) {
  if (!items?.length) return null;
  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
        <Icon size={16} />
        {title}
      </div>
      <ul className="space-y-1.5 list-disc list-inside text-sm text-gray-600">
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

function UploadCard({ onUploaded, uploadsUsed, maxUploads }) {
  const [uploading, setUploading] = useState(false);
  const atLimit = uploadsUsed >= maxUploads;

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const token = localStorage.getItem("nd-rest-tkn");
      if (token) client.defaults.headers.Authorization = `Bearer ${token}`;

      const fd = new FormData();
      fd.append("cv", file);

      const res = await client.post("/me/cv-submissions", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("CV uploaded — we're generating your review…");
      onUploaded(res.data);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Could not upload your CV.";
      toast.error(msg);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 text-center">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <PiFileTextLight size={30} className="text-gray-400" />
      </div>
      <h2 className="text-base font-semibold text-gray-800 mb-1">Upload your CV</h2>
      <p className="text-sm text-gray-500 mb-5 max-w-sm mx-auto">
        Get an AI-generated review — ATS readability, strengths, gaps, and suggested edits —
        plus a revised CV draft once you answer a few follow-up questions.
      </p>

      <input
        id="cvFileInput"
        type="file"
        accept=".pdf,.docx"
        onChange={handleFile}
        className="hidden"
        disabled={uploading || atLimit}
      />
      <label
        htmlFor="cvFileInput"
        className={`inline-block px-5 py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition-colors
          ${
            uploading || atLimit
              ? "bg-gray-200 text-gray-400 cursor-not-allowed pointer-events-none"
              : "bg-[#F05A19] hover:bg-[#d94e14] text-white"
          }`}
      >
        {uploading ? "Uploading…" : "Choose PDF or docx"}
      </label>

      {atLimit && (
        <p className="text-xs text-amber-600 mt-4 max-w-sm mx-auto">
          You've used all {maxUploads} of your CV reviews.
        </p>
      )}
    </div>
  );
}

function QuestionnaireCard({ submission, isRedraft, initialAnswers, initialSector, onCompleted }) {
  const questions = submission.followup_questions || [];
  const [answers, setAnswers] = useState(initialAnswers || {});
  const [targetSector, setTargetSector] = useState(initialSector || "");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    const missing = questions.some((q) => !answers[q.id]?.trim());
    if (missing) {
      toast.error("Please answer every question before submitting.");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("nd-rest-tkn");
      if (token) client.defaults.headers.Authorization = `Bearer ${token}`;

      const res = await client.post(`/me/cv-submissions/${submission.id}/revisions`, {
        answers,
        target_sector: targetSector.trim() || null,
      });
      toast.success(isRedraft ? "Redrafting your CV…" : "Drafting your revised CV…");
      onCompleted(res.data);
    } catch (err) {
      const msg = err?.response?.data?.message || "Could not draft your revised CV.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
      <div className="flex items-center gap-2 text-base font-semibold text-gray-800 mb-1">
        <PiPencilSimpleLineLight size={20} />
        {isRedraft ? "Redraft your CV" : "A few quick questions"}
      </div>
      <p className="text-sm text-gray-500 mb-5">
        {isRedraft
          ? "Update your answers, or point this draft at a different sector or career."
          : "These are tailored to the gaps in your review — your answers help draft a stronger CV."}
      </p>

      <div className="space-y-4">
        {questions.map((q) => (
          <div key={q.id}>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">{q.question}</label>
            <textarea
              rows={2}
              value={answers[q.id] || ""}
              onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700
                focus:outline-none focus:ring-2 focus:ring-[#F05A19]/30 focus:border-[#F05A19]"
            />
          </div>
        ))}

        <div>
          <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
            <PiTargetLight size={16} />
            Target a different sector or career (optional)
          </label>
          <input
            type="text"
            value={targetSector}
            onChange={(e) => setTargetSector(e.target.value)}
            placeholder="e.g. Product Management, Fintech, UX Design"
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700
              focus:outline-none focus:ring-2 focus:ring-[#F05A19]/30 focus:border-[#F05A19]"
          />
        </div>
      </div>

      <button
        onClick={handleSubmit}
        disabled={submitting}
        className="mt-5 w-full sm:w-auto px-5 py-2.5 rounded-xl text-sm font-semibold
          bg-[#F05A19] hover:bg-[#d94e14] text-white transition-colors disabled:opacity-60"
      >
        {submitting ? "Drafting your revised CV…" : isRedraft ? "Submit redraft" : "Submit answers"}
      </button>
    </div>
  );
}

function RevisedCvCard({ submissionId, revision }) {
  const [downloading, setDownloading] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const base = `${BACKEND}/api/me/cv-submissions/${submissionId}/revisions/${revision.id}`;

  const handleDownload = async (format) => {
    setDownloading(format);
    try {
      const res = await fetch(`${base}/download/${format}`, {
        headers: { Authorization: client.defaults.headers.Authorization || "" },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `revised-cv.${format}`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error("Could not download your revised CV. Please try again.");
    } finally {
      setDownloading(null);
    }
  };

  const handlePreview = async () => {
    const tab = window.open("", "_blank");
    setPreviewing(true);
    try {
      const res = await fetch(`${base}/preview`, {
        headers: { Authorization: client.defaults.headers.Authorization || "" },
      });
      if (!res.ok) throw new Error("Preview failed");
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      if (tab) {
        tab.location.href = blobUrl;
      } else {
        window.open(blobUrl, "_blank");
      }
    } catch {
      tab?.close();
      toast.error("Could not preview your revised CV. Please try again.");
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
      <div className="flex items-center gap-2 text-base font-semibold text-gray-800 mb-1">
        <PiCheckCircleLight size={20} className="text-emerald-600" />
        Your revised CV is ready
      </div>
      {revision.target_sector && (
        <div className="inline-flex items-center gap-1.5 bg-blue-50 text-[#294a93] text-xs font-semibold px-2.5 py-1 rounded-full mb-3">
          <PiTargetLight size={14} />
          Targeting: {revision.target_sector}
        </div>
      )}
      <p className="text-sm text-gray-500 mb-5">Preview it, or download it in the format you need.</p>

      <button
        onClick={handlePreview}
        disabled={previewing}
        className="w-full flex items-center justify-center gap-1.5 bg-gray-50 text-gray-700 text-sm
          font-medium py-2.5 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors
          disabled:opacity-60 mb-3"
      >
        <PiEyeLight size={16} />
        {previewing ? "Opening preview…" : "Preview PDF"}
      </button>

      <div className="flex gap-3">
        <button
          onClick={() => handleDownload("pdf")}
          disabled={downloading === "pdf"}
          className="flex-1 flex items-center justify-center gap-1.5 bg-[#F05A19] text-white text-sm
            font-medium py-2.5 rounded-lg hover:bg-[#d94e14] transition-colors disabled:opacity-60"
        >
          <PiDownloadSimpleLight size={16} />
          {downloading === "pdf" ? "Downloading…" : "Download PDF"}
        </button>
        <button
          onClick={() => handleDownload("docx")}
          disabled={downloading === "docx"}
          className="flex-1 flex items-center justify-center gap-1.5 border border-[#F05A19] text-[#F05A19]
            text-sm font-medium py-2.5 rounded-lg hover:bg-[#FEF0EB] transition-colors disabled:opacity-60"
        >
          <PiDownloadSimpleLight size={16} />
          {downloading === "docx" ? "Downloading…" : "Download docx"}
        </button>
      </div>
    </div>
  );
}

function RedraftSection({ submission, latestRevision, redraftsUsed, maxRedrafts, onCompleted }) {
  const [open, setOpen] = useState(false);
  const atLimit = redraftsUsed >= maxRedrafts;

  if (!open) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm font-semibold text-gray-800">Not satisfied with this draft?</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {atLimit ? `You've used all ${maxRedrafts} redrafts for this review.` : `${redraftsUsed} of ${maxRedrafts} redrafts used.`}
          </p>
        </div>
        <button
          onClick={() => setOpen(true)}
          disabled={atLimit}
          className="px-4 py-2 rounded-lg text-sm font-semibold border border-[#F05A19] text-[#F05A19]
            hover:bg-[#FEF0EB] transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400"
        >
          Redraft this CV
        </button>
      </div>
    );
  }

  return (
    <QuestionnaireCard
      submission={submission}
      isRedraft
      initialAnswers={latestRevision.followup_answers}
      initialSector={latestRevision.target_sector}
      onCompleted={onCompleted}
    />
  );
}

export default function CvReviewPage() {
  const [submission, setSubmission] = useState(null);
  const [enrolled, setEnrolled] = useState(true);
  const [limits, setLimits] = useState({ uploadsUsed: 0, maxUploads: 3, redraftsUsed: 0, maxRedrafts: 3 });
  const [loading, setLoading] = useState(true);

  const applyLatest = (payload) => {
    setEnrolled(payload.enrolled ?? true);
    setSubmission(payload.data || null);
    setLimits({
      uploadsUsed: payload.uploads_used ?? 0,
      maxUploads: payload.max_uploads ?? 3,
      redraftsUsed: payload.redrafts_used ?? 0,
      maxRedrafts: payload.max_redrafts ?? 3,
    });
  };

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;

    try {
      const res = await client.get("/me/cv-submissions/latest");
      applyLatest(res.data);
    } catch {
      toast.error("Could not load your CV review.");
    }
  }, []);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const latestRevision = submission?.revisions?.[0] || null;
  const isReviewPending = submission?.status === "processing";
  const isRevisionPending = latestRevision?.status === "pending";

  useEffect(() => {
    if (!isReviewPending && !isRevisionPending) return undefined;

    const interval = setInterval(refresh, 4000);
    return () => clearInterval(interval);
  }, [isReviewPending, isRevisionPending, refresh]);

  return (
    <div className="py-8 px-4 md:px-8 max-w-3xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">CV Review</h1>
        <p className="text-sm text-gray-500 mt-1">
          Upload your CV for AI-generated feedback, then get a revised draft.
        </p>
        {!loading && enrolled && (
          <div className="inline-flex items-center gap-1.5 bg-orange-50 text-[#F05A19] text-xs font-semibold px-3 py-1.5 rounded-full mt-3">
            <PiFileTextLight size={14} />
            You have {limits.maxUploads} attempt{limits.maxUploads === 1 ? "" : "s"} to review your CV
            {limits.uploadsUsed > 0 && ` — ${Math.max(limits.maxUploads - limits.uploadsUsed, 0)} remaining`}
          </div>
        )}
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-3" />
          <div className="h-3 bg-gray-100 rounded w-2/3" />
        </div>
      ) : !enrolled ? (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <PiFileTextLight size={30} className="text-gray-400" />
          </div>
          <h2 className="text-base font-semibold text-gray-800 mb-1">Enroll to access CV Review</h2>
          <p className="text-sm text-gray-500 max-w-sm mx-auto">
            CV Review is available to students enrolled in a cohort. Enroll in a course to get AI-generated feedback on your CV.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {(!submission || submission.status === "failed") && (
            <>
              {submission?.status === "failed" && (
                <div className="flex items-start gap-2 bg-rose-50 border border-rose-100 text-rose-700 text-sm rounded-xl p-4">
                  <PiWarningCircleLight size={18} className="mt-0.5 flex-shrink-0" />
                  {submission.failure_reason}
                </div>
              )}
              <UploadCard
                onUploaded={applyLatest}
                uploadsUsed={limits.uploadsUsed}
                maxUploads={limits.maxUploads}
              />
            </>
          )}

          {isReviewPending && (
            <ProcessingCard
              title="Analyzing your CV…"
              subtitle="This usually takes a minute or two. You can leave this page — your review will be waiting when you come back."
            />
          )}

          {submission && submission.status === "reviewed" && (
            <>
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
                  <h2 className="text-base font-semibold text-gray-800">Your review</h2>
                  <ScoreBadge score={submission.ats_score} />
                </div>
                <div className="space-y-3">
                  <ListSection icon={PiCheckCircleLight} title="Strengths" items={submission.strengths} />
                  <ListSection icon={PiWarningCircleLight} title="Gaps" items={submission.gaps} />
                  <ListSection icon={PiPencilSimpleLineLight} title="Suggested edits" items={submission.suggested_edits} />
                </div>
              </div>

              {!latestRevision ? (
                <QuestionnaireCard submission={submission} isRedraft={false} onCompleted={refresh} />
              ) : isRevisionPending ? (
                <ProcessingCard
                  title="Drafting your revised CV…"
                  subtitle="This usually takes a minute or two. You can leave this page — your draft will be waiting when you come back."
                />
              ) : latestRevision.status === "failed" ? (
                <>
                  <div className="flex items-start gap-2 bg-rose-50 border border-rose-100 text-rose-700 text-sm rounded-xl p-4">
                    <PiWarningCircleLight size={18} className="mt-0.5 flex-shrink-0" />
                    {latestRevision.failure_reason || "Something went wrong drafting your revised CV. Please try again."}
                  </div>
                  <QuestionnaireCard
                    submission={submission}
                    isRedraft={false}
                    initialAnswers={latestRevision.followup_answers}
                    initialSector={latestRevision.target_sector}
                    onCompleted={refresh}
                  />
                </>
              ) : (
                <>
                  <RevisedCvCard submissionId={submission.id} revision={latestRevision} />
                  <RedraftSection
                    submission={submission}
                    latestRevision={latestRevision}
                    redraftsUsed={limits.redraftsUsed}
                    maxRedrafts={limits.maxRedrafts}
                    onCompleted={refresh}
                  />
                </>
              )}
            </>
          )}

          <FeatureFeedbackBox feature="cv_review" />
        </div>
      )}
    </div>
  );
}
